import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Headset, ShieldCheck, CloudCog, Network, DatabaseBackup, Compass } from "lucide-react";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { IMAGES, SERVICES } from "@/lib/data";

const ICONS = { Headset, ShieldCheck, CloudCog, Network, DatabaseBackup, Compass };

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <>
      <PageHead
        eyebrow="Services"
        title="Everything a growing business needs from IT. Nothing it doesn't."
        sub="Six core services, each explained in everyday words and priced around outcomes, not acronyms."
        testid="services-page-title"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-border">
            <img
              src={IMAGES.server}
              alt="Secure server and network infrastructure managed by Groundbreaking IT"
              className="h-64 sm:h-80 w-full object-cover"
              data-testid="services-hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" aria-hidden="true" />
            <p className="absolute bottom-5 left-6 font-mono text-xs uppercase tracking-[0.2em] text-white/90">
              Monitored around the clock, supported by real people
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.id}>
              <article
                id={s.id}
                data-testid={`service-detail-${s.id}`}
                className="scroll-mt-24 rounded-3xl border border-border bg-card p-8 sm:p-10 transition-colors duration-300 hover:border-primary/40"
              >
                <div className="grid gap-8 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    <span className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-forest text-blue-300">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{s.title}</h2>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.plain}</p>
                    <p className="mt-5 flex items-start gap-2.5 rounded-xl bg-muted/60 p-4 text-sm font-medium">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                      {s.outcome}
                    </p>
                  </div>
                  <div className="lg:col-span-7 lg:border-l lg:border-border lg:pl-10">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
                      What's included
                    </p>
                    <ul className="grid gap-3.5 sm:grid-cols-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact?intent=review"
                      data-testid={`service-cta-${s.id}`}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline underline-offset-4"
                    >
                      Talk to us about {s.title.toLowerCase()}
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      <CtaSection />
    </>
  );
}
